import { className, spread, mergeProps, template, createComponent, use, insert, effect, setAttribute, classList, render } from 'solid-js/web';
import awaitImage from 'await-res';
import { onCleanup, splitProps, createSignal, createEffect, on, createContext, createMemo, Show, onMount, untrack, batch, lazy, useContext } from 'solid-js';
import { autoUpdate, computePosition } from '@floating-ui/dom';
import { createRuntimeFn } from '@vanilla-extract/recipes/createRuntimeFn';

function useTimeout() {
  let timer;
  const clear = () => {
    clearTimeout(timer);
  };
  const set = (handler, timeout) => {
    clear();
    timer = setTimeout(handler, timeout);
  };
  onCleanup(clear);
  return set;
}

var styleBase = '_17tlpra0 fidrkx2';
var styleOpenned = '_17tlpra1';

const _tmpl$$4 = /* @__PURE__ */ template(`<div role=dialog>`);
function Modal(props) {
  const [localProp, forwardProp] = splitProps(props, ["open", "onClose", "delay", "style"]);
  const setTimeout = useTimeout();
  const [visibility, setVisibility] = createSignal(localProp.open);
  const handleEscape = (e) => {
    if (e.key === "Escape") {
      localProp.onClose();
    }
  };
  createEffect(on(() => localProp.open, () => {
    if (visibility() !== localProp.open) {
      if (localProp.open) {
        setVisibility(true);
        window.addEventListener("keydown", handleEscape);
        onCleanup(() => window.removeEventListener("keydown", handleEscape));
      } else {
        setTimeout(() => setVisibility(false), localProp.delay ?? 500);
      }
    }
  }, {
    defer: true
  }));
  return (() => {
    const _el$ = _tmpl$$4();
    className(_el$, styleBase);
    spread(_el$, mergeProps({
      get ["on:click"]() {
        return localProp.onClose;
      },
      get style() {
        return {
          visibility: visibility() ? void 0 : "hidden",
          ...localProp.style
        };
      },
      get classList() {
        return {
          [styleOpenned]: localProp.open
        };
      }
    }, forwardProp), false, false);
    return _el$;
  })();
}

var styleGappedLine = 'fidrkx4';
var styleIcon = 'fidrkx5';
var styleTransition = 'fidrkx8';
var styleButtonInline = 'fidrkxb fidrkxa fidrkx3 fidrkx8';
var overflowHidden = 'fidrkxc';
var stableScrollbar = 'fidrkxd';

function stopPropagation(e) {
  e.stopPropagation();
}
function preventScroll() {
  const classList = document.documentElement.classList;
  if (document.body.scrollHeight > window.innerHeight) {
    classList.add(overflowHidden, stableScrollbar);
  } else
    classList.add(overflowHidden);
}
function allowScroll() {
  document.documentElement.classList.remove(overflowHidden, stableScrollbar);
}

var __default__ = '_1fwbbnf0 fidrkx6 fidrkx8';

const FullscreenImageContext = createContext();

const PathHideImage = "m840-234-80-80v-446H314l-80-80h526q33 0 56.5 23.5T840-760v526ZM792-56l-64-64H200q-33 0-56.5-23.5T120-200v-528l-64-64 56-56 736 736-56 56ZM240-280l120-160 90 120 33-44-283-283v447h447l-80-80H240Zm297-257ZM424-424Z";

function drawBrokenImageIndicator(ctx, size) {
  const path = new Path2D(PathHideImage);
  const scaleFactor = size / 980;
  ctx.save();
  ctx.scale(scaleFactor, scaleFactor);
  ctx.translate(0, 980);
  ctx.fillStyle = "#7777";
  ctx.fill(path);
  ctx.restore();
}

const _tmpl$$3 = /* @__PURE__ */ template(`<canvas>`);
const modalPadding = 10;
function useDocumentClientSize() {
  const [size, setSize] = createSignal([0, 0]);
  const callback = () => {
    setSize([document.documentElement.clientWidth, document.documentElement.clientHeight]);
  };
  onMount(() => {
    callback();
    window.addEventListener("resize", callback);
  });
  onCleanup(() => {
    window.removeEventListener("resize", callback);
  });
  return size;
}
function FullscreenImage(props) {
  let ref;
  const [localProp, propForward] = splitProps(props, ["img", "after", "before", "onClose"]);
  const [fullscreen, setFullscreen] = createSignal(false);
  const [rect, setRect] = createSignal();
  const [enableTransition, setEnableTransition] = createSignal(false);
  const clientSize = useDocumentClientSize();
  const refreshPos = () => {
    setRect(localProp.img?.getBoundingClientRect());
  };
  const targetSize = createMemo(() => {
    if (!localProp.img) return [0, 0];
    const {
      naturalWidth,
      naturalHeight
    } = localProp.img;
    const [clientWidth, clientHeight] = clientSize();
    let targetHeight = clientHeight - modalPadding * 4;
    let targetWidth = targetHeight * naturalWidth / naturalHeight;
    if (targetWidth > clientWidth) {
      targetWidth = clientWidth - modalPadding * 4;
      targetHeight = targetWidth / naturalWidth * naturalHeight;
    }
    return [targetWidth, targetHeight];
  });
  const showSlot = () => !enableTransition() && fullscreen();
  const ctxValue = {
    ref: () => ref,
    img: () => props.img
  };
  function drawOnCanvas() {
    const ctx = ref.getContext("2d");
    const [targetWidth, targetHeight] = targetSize();
    ctx.clearRect(0, 0, ref.width, ref.height);
    if (isNaN(targetWidth) || isNaN(targetHeight)) {
      const size = 200;
      const scaledSize = size * window.devicePixelRatio;
      ref.width = scaledSize;
      ref.height = scaledSize;
      drawBrokenImageIndicator(ctx, scaledSize);
      return;
    }
    ref.width = targetWidth * window.devicePixelRatio;
    ref.height = targetHeight * window.devicePixelRatio;
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    ctx.drawImage(props.img, 0, 0, targetWidth, targetHeight);
  }
  createEffect((prev) => {
    if (localProp.img !== prev && localProp.img) {
      const ctx = ref.getContext("2d");
      setEnableTransition(false);
      awaitImage(localProp.img).then(() => {
        drawOnCanvas();
        if (!fullscreen()) {
          refreshPos();
          setTimeout(() => {
            setEnableTransition(true);
            setFullscreen(true);
          }, 0);
        }
      });
      ctx.clearRect(0, 0, ref.width, ref.height);
    }
  }, localProp.img);
  createEffect(() => {
    if (!fullscreen()) return;
    drawOnCanvas();
  });
  createEffect(() => {
    if (fullscreen() || enableTransition()) {
      preventScroll();
      onCleanup(() => {
        allowScroll();
      });
    }
  });
  return createComponent(Modal, {
    get open() {
      return fullscreen();
    },
    onClose: () => {
      refreshPos();
      setEnableTransition(true);
      setFullscreen(false);
      props.onClose?.(ctxValue);
    },
    delay: 200,
    get children() {
      return createComponent(FullscreenImageContext.Provider, {
        value: ctxValue,
        get children() {
          return [createComponent(Show, {
            get when() {
              return showSlot();
            },
            get children() {
              return localProp.before;
            }
          }), (() => {
            const _el$ = _tmpl$$3();
            _el$.addEventListener("transitionend", () => {
              setEnableTransition(false);
            });
            const _ref$ = ref;
            typeof _ref$ === "function" ? use(_ref$, _el$) : ref = _el$;
            className(_el$, __default__);
            spread(_el$, mergeProps({
              get style() {
                return {
                  "transition-property": enableTransition() ? "transform" : "none",
                  get transform() {
                    let [targetWidth, targetHeight] = targetSize();
                    if (isNaN(targetWidth) || isNaN(targetHeight)) {
                      targetWidth = 100;
                      targetHeight = 100;
                    }
                    if (fullscreen()) {
                      const [clientWidth, clientHeight] = clientSize();
                      return `translate(${(clientWidth - targetWidth) / 2}px,${(clientHeight - targetHeight) / 2}px) scale(${1 / window.devicePixelRatio})`;
                    } else {
                      if (!rect()) return "";
                      const {
                        x,
                        y,
                        height
                      } = rect();
                      const {
                        naturalWidth,
                        naturalHeight
                      } = localProp.img;
                      const ratio = naturalWidth / naturalHeight || 1;
                      const scaleX = ratio * height / targetWidth / window.devicePixelRatio;
                      const scaleY = height / targetHeight / window.devicePixelRatio;
                      return `translate(${x}px,${y}px) scale(${scaleX},${scaleY})`;
                    }
                  }
                };
              }
            }, propForward), false, false);
            return _el$;
          })(), createComponent(Show, {
            get when() {
              return showSlot();
            },
            get children() {
              return localProp.after;
            }
          })];
        }
      });
    }
  });
}

var styleBigButton = '_15vhptq0 fidrkxa fidrkx3 fidrkx8';
var styleIndicatorMoving = '_15vhptq1';
var styleIndicator = '_15vhptq2 fidrkx3 fidrkx8';

const _tmpl$$2 = /* @__PURE__ */ template(`<button>`), _tmpl$2 = /* @__PURE__ */ template(`<div>/`);
function GalleryMoveButton(props) {
  return (() => {
    const _el$ = _tmpl$$2();
    className(_el$, styleBigButton);
    _el$.addEventListener("click", (e) => props.disabled ? e.stopPropagation() : props.onClick(e));
    insert(_el$, () => props.toRight ? "►" : "◄");
    effect((_p$) => {
      const _v$ = props.disabled || void 0, _v$2 = props.toRight || void 0, _v$3 = props.toRight ? "下一张" : "上一张";
      _v$ !== _p$._v$ && setAttribute(_el$, "aria-disabled", _p$._v$ = _v$);
      _v$2 !== _p$._v$2 && setAttribute(_el$, "data-right", _p$._v$2 = _v$2);
      _v$3 !== _p$._v$3 && setAttribute(_el$, "title", _p$._v$3 = _v$3);
      return _p$;
    }, {
      _v$: void 0,
      _v$2: void 0,
      _v$3: void 0
    });
    return _el$;
  })();
}
function PositionIndicator(props) {
  const setTimeout = useTimeout();
  const [moving, setMoving] = createSignal(false);
  createEffect(() => {
    if (props.delta) {
      setMoving(true);
      setTimeout(() => setMoving(false), 500);
    }
  });
  return (() => {
    const _el$2 = _tmpl$2(), _el$3 = _el$2.firstChild;
    className(_el$2, styleIndicator);
    insert(_el$2, () => props.curr, _el$3);
    insert(_el$2, () => props.total, null);
    effect((_$p) => classList(_el$2, {
      [styleIndicatorMoving]: moving()
    }, _$p));
    return _el$2;
  })();
}
function Gallery(props) {
  const [currIndex, setCurrIndex] = createSignal(props.images.findIndex((img) => img === props.curr));
  createEffect(() => {
    if (props.images[untrack(currIndex)] !== props.curr) {
      setCurrIndex(props.images.findIndex((img) => img === props.curr));
    }
  });
  const noNext = () => currIndex() >= props.images.length - 1;
  const noPrev = () => currIndex() <= 0;
  const [delta, setDelta] = createSignal(0);
  const move = /* @__PURE__ */ (() => {
    let t;
    const deltaMax = () => props.images.length - 1 - currIndex();
    const deltaMin = () => -currIndex();
    return (direction) => {
      if (t !== void 0) cancelAnimationFrame(t);
      setDelta((prev) => {
        const next = prev + direction;
        if (next > deltaMax()) return deltaMax();
        if (next < deltaMin()) return deltaMin();
        return next;
      });
      t = requestAnimationFrame(() => {
        t = void 0;
        if (delta() !== 0) {
          const nextIndex = currIndex() + delta();
          batch(() => {
            setCurrIndex(nextIndex);
            setDelta(0);
          });
          props.onChange(props.images[nextIndex]);
        }
      });
    };
  })();
  const handleKeydown = (e) => {
    switch (e.code) {
      case "ArrowRight":
      case "ArrowDown":
      case "KeyD":
        move(1);
        break;
      case "ArrowLeft":
      case "ArrowUp":
      case "KeyA":
        move(-1);
        break;
      default:
        return;
    }
    e.preventDefault();
  };
  onMount(() => {
    window.addEventListener("keydown", handleKeydown);
  });
  onCleanup(() => {
    window.removeEventListener("keydown", handleKeydown);
  });
  return [createComponent(GalleryMoveButton, {
    get disabled() {
      return noPrev();
    },
    onClick: (e) => {
      e.stopPropagation();
      move(-1);
    }
  }), createComponent(GalleryMoveButton, {
    get disabled() {
      return noNext();
    },
    toRight: true,
    onClick: (e) => {
      e.stopPropagation();
      move(1);
    }
  }), createComponent(PositionIndicator, {
    get total() {
      return props.images.length;
    },
    get curr() {
      return currIndex() + 1;
    },
    get delta() {
      return delta();
    }
  })];
}

var popoverContentVarName = '--vsxlub0';
var stylePopoverReferenceElement = createRuntimeFn({defaultClassName:'vsxlub1',variantClassNames:{placement:{top:'vsxlub2',right:'vsxlub3'},trigger:{hover:'vsxlub4'}},defaultVariants:{placement:'top'},compoundVariants:[]});

function useFloating(ref, self, options) {
  const [style, setStyle] = createSignal({});
  createEffect(() => {
    if (!ref() || !self()) return;
    async function updatePostion() {
      const { x, y } = await computePosition(ref(), self(), options);
      setStyle({ top: y + "px", left: x + "px" });
    }
    const cleanup = autoUpdate(ref(), self(), updatePostion);
    onCleanup(cleanup);
  });
  return {
    get style() {
      return style();
    }
  };
}
function usePopover(content, placement, byHover) {
  const value = () => {
    const c = content();
    if (c) {
      return `'${c}'`;
    } else {
      return "none";
    }
  };
  return [
    () => stylePopoverReferenceElement({ placement, trigger: byHover ? "hover" : void 0 }),
    () => ({ [popoverContentVarName]: value() })
  ];
}

var styleFloatingButtonGroup = 'ni4w940 fidrkx6 fidrkx3 fidrkx7';
var styleFloatingButton = 'ni4w941 fidrkx6 fidrkx9 fidrkx8';

const _tmpl$$1 = /* @__PURE__ */ template(`<button>`);
function FloatingButton(props) {
  const [local, forward] = splitProps(props, ["icon", "on:click"]);
  const [className, style] = usePopover(() => props.title, "right", true);
  return (() => {
    const _el$ = _tmpl$$1();
    _el$.addEventListener("click", (e) => {
      e.stopPropagation(), !props.disabled && local["on:click"]?.(e);
    });
    spread(_el$, mergeProps({
      get ["class"]() {
        return styleFloatingButton + " " + className();
      },
      get innerHTML() {
        return local.icon;
      },
      get style() {
        return style();
      },
      get ["aria-disabled"]() {
        return props.disabled || void 0;
      }
    }, forward), false, false);
    return _el$;
  })();
}

const Icon$1 = "<svg xmlns=\"http://www.w3.org/2000/svg\" height=\"24px\" viewBox=\"0 -960 960 960\" width=\"24px\" fill=\"currentColor\"><path d=\"M440-280h80v-240h-80v240Zm40-320q17 0 28.5-11.5T520-640q0-17-11.5-28.5T480-680q-17 0-28.5 11.5T440-640q0 17 11.5 28.5T480-600Zm0 520q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320Z\"/></svg>";

const ExifPannel = lazy(() => import('./pannel-OkKfWslg.mjs'));
function ExifInfoButton() {
  const ctx = useContext(FullscreenImageContext);
  const [buttonRef, setButtonRef] = createSignal();
  const [pannelRef, setPannelRef] = createSignal();
  const [show, setShow] = createSignal(false);
  return createComponent(Show, {
    when: ctx,
    get children() {
      return [createComponent(FloatingButton, {
        ref: setButtonRef,
        title: "图片信息",
        icon: Icon$1,
        "on:click": (e) => {
          e.stopPropagation();
          setShow((prev) => !prev);
        }
      }), createComponent(Show, {
        get when() {
          return show();
        },
        get children() {
          return createComponent(ExifPannel, mergeProps({
            ref: setPannelRef,
            get img() {
              return ctx.img();
            }
          }, () => useFloating(buttonRef, pannelRef, {
            placement: "right-start"
          })));
        }
      })];
    }
  });
}

const _tmpl$ = /* @__PURE__ */ template(`<div>`);
function FloatingButtonGroup(props) {
  const [local, forward] = splitProps(props, ["class"]);
  return (() => {
    const _el$ = _tmpl$();
    spread(_el$, mergeProps({
      get ["class"]() {
        return styleFloatingButtonGroup + (local.class ? " " + local.class : "");
      }
    }, forward), false, false);
    return _el$;
  })();
}

const Icon = "<svg xmlns=\"http://www.w3.org/2000/svg\" height=\"24px\" viewBox=\"0 -960 960 960\" width=\"24px\" fill=\"currentColor\"><path d=\"M120-120v-240h80v104l124-124 56 56-124 124h104v80H120Zm516-460-56-56 124-124H600v-80h240v240h-80v-104L636-580Z\"/></svg>";

const FullscreenDisabled = (() => {
  const el = document.createElement("img");
  return !("requestFullscreen" in el);
})();
function FullscreenViewButton() {
  const ctx = useContext(FullscreenImageContext);
  return createComponent(FloatingButton, {
    disabled: FullscreenDisabled,
    title: "全屏显示",
    icon: Icon,
    "on:click": () => {
      ctx?.img()?.requestFullscreen();
    }
  });
}

function deployLightbox({
  img,
  before,
  after
}, container) {
  return render(() => createComponent(FullscreenImage, {
    get img() {
      return img();
    },
    get before() {
      return before?.();
    },
    get after() {
      return after?.();
    }
  }), container);
}
function createGalleryInstance(images, curr, onChange) {
  return createComponent(Gallery, {
    get images() {
      return images();
    },
    get curr() {
      return curr();
    },
    onChange
  });
}
function ActionGroup() {
  return createComponent(FloatingButtonGroup, {
    get children() {
      return [createComponent(ExifInfoButton, {}), createComponent(FullscreenViewButton, {})];
    }
  });
}
function createActionGroupInstance() {
  return createComponent(ActionGroup, {});
}
function deployOnWordpress() {
  const entryContent = document.querySelector("div.entry-content");
  if (entryContent) {
    const imgs = entryContent.querySelectorAll("img:not(.avatar)");
    if (imgs.length > 0) {
      const node = document.body.appendChild(document.createElement("div"));
      const [img, setImg] = createSignal(void 0, {
        equals: false
      });
      entryContent.addEventListener("click", (e) => {
        const {
          target
        } = e;
        if (target instanceof HTMLImageElement) {
          if (target.classList.contains("avatar")) {
            return;
          }
          setImg(target);
        }
      });
      const getImages = () => Array.from(imgs);
      deployLightbox({
        img,
        after: () => createGalleryInstance(getImages, img, setImg),
        before: () => createActionGroupInstance()
      }, node);
    }
  }
}

export { ExifInfoButton as E, FullscreenImage as F, Gallery as G, styleGappedLine as a, styleIcon as b, useTimeout as c, styleButtonInline as d, stopPropagation as e, deployLightbox as f, createGalleryInstance as g, createActionGroupInstance as h, deployOnWordpress as i, styleTransition as s, usePopover as u };
//# sourceMappingURL=index-nWSNO-RD.mjs.map
